import { apiSlice } from './apiSlice'

/** Mirrors FastAPI `SaleReleaseOrderOut` (camelCase aliases) — frmCtrlSalesQryRO datasheet. */
export interface SaleReleaseOrder {
  id: number
  customerInvoiceNo: string | null
  releaseNo: string | null
  releaseDate: string | null
  sendToWarehouse: string | null
  containerNo: string | null
  releasableNetWeight: number | null
  releaseNetWeight: number | null
  releaseGrossWeight: number | null
  releaseBag: number | null
  releasableGrossWeight: number | null
  releasableBag: number | null
  whRef: string | null
  contractRef: string | null
  buyersRef: string | null
  deliveryOrder: string | null
  icoNo: string | null
  commodity: string | null
  blNumber: string | null
  blDate: string | null
  loadingPort: string | null
  dischargePort: string | null
  vesselVoyage: string | null
  lstDeliveryTerms: string | null
  whRange: string | null
  whStorageDeadline: string | null
  invoiceQuantity: number | null
  note: string | null
  docsDispatch: string | null
  deliveryFrom: string | null
  isFinalized: number | null
  personUpdate: string | null
  dateUpdate: string | null
}

export interface SaleReleaseOrderListResponse {
  items: SaleReleaseOrder[]
  total: number
  page: number
  pageSize: number
}

export interface SaleReleaseOrderListQuery {
  page: number
  pageSize: number
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

/** frmCtrlSalesQryROAdd_upgrade New/Edit payload — camelCase → release_order_scs. */
export interface SaleReleaseOrderDetailWrite {
  invoiceNo?: string | null
  deliveryOrder?: string | null
  containerNo?: string | null
  nwRelease?: number | null
  gwRelease?: number | null
  bagRelease?: number | null
}
export interface SaleReleaseOrderWrite {
  customerInvoiceNo?: string | null
  releaseNo?: string | null
  releaseDate?: string | null
  sendToWarehouse?: string | null
  whStorageDeadline?: string | null
  whRange?: string | null
  note?: string | null
  details?: SaleReleaseOrderDetailWrite[] | null
}

/** GET /{id}/form shape for Edit hydrate. */
export type SaleReleaseOrderForm = Record<string, unknown> & { id: number }

/** frmCtrlSalesQryROAdd_upgrade_detail — Release Details datasheet row. */
export interface SaleReleaseOrderDetailRow {
  id: number
  releaseNo: string | null
  invoiceNo: string | null
  deliveryOrder: string | null
  containerNo: string | null
  nwRelease: number | null
  gwRelease: number | null
  bagRelease: number | null
  package: string | null
  icoNo: string | null
  dateUpdated: string | null
  personUpdated: string | null
}
export interface SaleReleaseOrderDetailListResponse {
  items: SaleReleaseOrderDetailRow[]
  total: number
}

/** Container picker row (v_invoice_lst_container) for the New/Edit dialog. */
export interface ReleasableContainer {
  id: number
  invoice_no: string | null
  delivery_order: string | null
  container_no: string | null
  nw_cont: number | null
  nw_releasable: number | null
  gw_releasable: number | null
  bag_releasable: number | null
}

export interface RoLookupItem { value: string; label: string; meta?: Record<string, unknown> | null }
export interface RoLookupResponse { items: RoLookupItem[]; total: number }
export interface SuggestReleaseNoResponse { releaseNo: string | null; exists: boolean }

const saleReleaseOrderApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleReleaseOrders: builder.query<SaleReleaseOrderListResponse, SaleReleaseOrderListQuery>({
      query: ({ page, pageSize, shipmentPeriodFrom, shipmentPeriodTo, finalizedFilter }) => ({
        url: '/sale-release-orders',
        params: {
          page,
          pageSize,
          ...(shipmentPeriodFrom ? { shipmentPeriodFrom } : {}),
          ...(shipmentPeriodTo ? { shipmentPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleReleaseOrder'],
    }),

    getSaleReleaseOrder: builder.query<SaleReleaseOrder, number>({
      query: (id) => `/sale-release-orders/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleReleaseOrder', id }],
    }),

    deleteSaleReleaseOrder: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-release-orders/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleReleaseOrder'],
    }),

    getSaleReleaseOrderCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-release-orders/access/can-delete',
    }),

    // ── frmCtrlSalesQryROAdd_upgrade ──────────────────────────────────────
    getSaleReleaseOrderForm: builder.query<SaleReleaseOrderForm, number>({
      query: (id) => `/sale-release-orders/${id}/form`,
    }),
    createSaleReleaseOrder: builder.mutation<{ release_order_scs_id: number }, SaleReleaseOrderWrite>({
      query: (body) => ({ url: '/sale-release-orders', method: 'POST', body }),
      invalidatesTags: ['SaleReleaseOrder'],
    }),
    updateSaleReleaseOrder: builder.mutation<
      { release_order_scs_id: number },
      { id: number; body: SaleReleaseOrderWrite }
    >({
      query: ({ id, body }) => ({ url: `/sale-release-orders/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleReleaseOrder'],
    }),
    suggestReleaseNo: builder.query<SuggestReleaseNoResponse, string>({
      query: (customerInvoiceNo) => ({
        url: '/sale-release-orders/suggest/release-no',
        params: { customerInvoiceNo },
      }),
      keepUnusedDataFor: 0,
    }),
    checkReleaseNo: builder.query<SuggestReleaseNoResponse, { releaseNo: string; excludeId?: number }>({
      query: ({ releaseNo, excludeId }) => ({
        url: '/sale-release-orders/check/release-no',
        params: { releaseNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getReleaseOrderDetails: builder.query<SaleReleaseOrderDetailListResponse, string>({
      query: (releaseNo) => ({ url: '/sale-release-orders/details', params: { releaseNo } }),
      keepUnusedDataFor: 0,
    }),
    getReleasableContainers: builder.query<ReleasableContainer[], string>({
      query: (customerInvoiceNo) => ({
        url: '/sale-release-orders/releasable-containers',
        params: { customerInvoiceNo },
      }),
      keepUnusedDataFor: 0,
    }),
    // Lookups
    roLookupCustomerInvoices: builder.query<RoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-release-orders/lookups/customer-invoices', params: q ? { q } : {} }),
    }),
    roLookupSendToWarehouse: builder.query<RoLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-release-orders/lookups/send-to-warehouse', params: q ? { q } : {} }),
    }),
    // Print — rpCtrlSalesRO (header + N detail rows) → web HTML preview.
    getReleaseOrderPrintPreview: builder.query<{ title: string; html: string; message?: string }, string>({
      query: (releaseNo) => ({ url: '/sale-release-orders/print/preview', params: { releaseNo } }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useGetSaleReleaseOrdersQuery,
  useGetSaleReleaseOrderQuery,
  useDeleteSaleReleaseOrderMutation,
  useGetSaleReleaseOrderCanHardDeleteQuery,
  useLazyGetSaleReleaseOrderFormQuery,
  useCreateSaleReleaseOrderMutation,
  useUpdateSaleReleaseOrderMutation,
  useLazySuggestReleaseNoQuery,
  useLazyCheckReleaseNoQuery,
  useGetReleaseOrderDetailsQuery,
  useLazyGetReleaseOrderDetailsQuery,
  useLazyGetReleasableContainersQuery,
  useRoLookupCustomerInvoicesQuery,
  useRoLookupSendToWarehouseQuery,
  useLazyGetReleaseOrderPrintPreviewQuery,
} = saleReleaseOrderApi

export default saleReleaseOrderApi
